import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { OnboardingSection } from '../OnboardingSection';
import { useOnboardingPractitioner } from '../useOnboardingPractitioner';
import { completeProviderOnboardingSection } from '../../../lib/providerOnboardingService';
import { WeeklyAvailabilityEditor } from '../../../features/calendar/WeeklyAvailabilityEditor';
import {
  getDefaultProviderWeeklyAvailability,
} from '../../../features/calendar/providerAvailabilityTypes';
import {
  loadProviderWeeklyAvailability,
  saveProviderWeeklyAvailability,
} from '../../../features/calendar/providerAvailabilitySchedule';
import {
  availabilitySectionFormSchema,
  getProviderWeeklyAvailabilityUiDisableReason,
  type AvailabilitySectionFormValues,
} from '@/schemas/schedule.schema';

export function AvailabilitySection(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const practitioner = useOnboardingPractitioner();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const form = useForm<AvailabilitySectionFormValues>({
    initialValues: { weekly: getDefaultProviderWeeklyAvailability() },
    validate: zod4Resolver(availabilitySectionFormSchema),
  });

  const practitionerId = practitioner?.resourceType === 'Practitioner' ? practitioner.id : undefined;

  useEffect(() => {
    if (!practitionerId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    medplum.invalidateSearches('Schedule');
    void loadProviderWeeklyAvailability(medplum, practitionerId)
      .then(({ weekly }) => {
        if (!cancelled) {
          form.setValues({ weekly });
        }
      })
      .catch(() => {
        if (!cancelled) {
          form.setValues({ weekly: getDefaultProviderWeeklyAvailability() });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Hydrate once per practitioner; avoid re-running on form identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, practitionerId]);

  const disableReason = getProviderWeeklyAvailabilityUiDisableReason(form.values.weekly);

  const handleSave = form.onSubmit(
    async (values) => {
      if (!practitionerId) {
        notifications.show({
          color: 'red',
          title: 'Could not save availability',
          message: 'Provider profile is not loaded yet. Please try again.',
        });
        return;
      }

      setSaving(true);
      try {
        const practitionerDisplay =
          profile?.resourceType === 'Practitioner'
            ? formatPractitionerPreferredLabel(profile)
            : practitioner?.resourceType === 'Practitioner'
              ? formatPractitionerPreferredLabel(practitioner)
              : undefined;

        try {
          await saveProviderWeeklyAvailability(medplum, practitionerId, values.weekly, {
            practitionerDisplay: practitionerDisplay || undefined,
          });
          medplum.invalidateSearches('Schedule');
        } catch (error) {
          notifications.show({
            color: 'red',
            title: 'Could not save availability',
            message: error instanceof Error ? error.message : 'Please try again.',
          });
          return;
        }

        try {
          const sectionResult = await completeProviderOnboardingSection(medplum, 'availability');
          if (!sectionResult.ok) {
            const detail = sectionResult.missingSections
              .map((s) => `${s.title}: ${s.missingFields.join(', ')}`)
              .join('; ');
            notifications.show({
              color: 'red',
              title: 'Hours saved, but onboarding was not marked complete',
              message: detail || sectionResult.message,
            });
            return;
          }
        } catch (error) {
          notifications.show({
            color: 'red',
            title: 'Hours saved, but onboarding was not marked complete',
            message: error instanceof Error ? error.message : 'Please try again to finish this step.',
          });
          return;
        }

        medplum.invalidateSearches('Task');
        notifications.show({
          color: 'green',
          title: 'Availability saved',
          message: 'Your weekly schedule has been updated.',
        });
        navigate('/onboarding');
      } finally {
        setSaving(false);
      }
    },
    () => {
      notifications.show({
        color: 'red',
        title: 'Missing required fields',
        message: disableReason ?? 'Select at least one working day with valid hours.',
      });
    },
  );

  return (
    <OnboardingSection
      title="Availability"
      onSave={handleSave}
      isSaving={saving}
      saveDisabled={loading || Boolean(disableReason)}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Set the days and hours clients can book with you. You need at least one working day.
        </Text>
        <WeeklyAvailabilityEditor
          value={form.values.weekly}
          onChange={(weekly) => form.setFieldValue('weekly', weekly)}
          loading={loading}
        />
      </Stack>
    </OnboardingSection>
  );
}
